import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, Briefcase, TrendingUp, Building2, Award } from 'lucide-react';
import SEO from '../components/SEO';

const ExamComparison = () => {
  const comparisonStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "CIRO Exam Comparison Guide",
    "description": "Complete comparison of all CIRO certification exams in Canada: CIRE, RSE, ISE, and Derivatives",
    "numberOfItems": 4,
    "itemListElement": [
      {
        "@type": "Course",
        "position": 1,
        "name": "CIRE - Canadian Investment Regulatory Exam",
        "description": "Entry-level exam for Registered Representatives and Investment Representatives",
        "provider": {
          "@type": "EducationalOrganization",
          "name": "Vitta Finance Academy"
        }
      },
      {
        "@type": "Course",
        "position": 2,
        "name": "RSE - Retail Securities Exam",
        "description": "Advanced exam for investment advisors serving retail clients",
        "provider": {
          "@type": "EducationalOrganization",
          "name": "Vitta Finance Academy"
        }
      },
      {
        "@type": "Course",
        "position": 3,
        "name": "ISE - Institutional Securities Exam",
        "description": "Specialized exam for professionals serving institutional clients",
        "provider": {
          "@type": "EducationalOrganization",
          "name": "Vitta Finance Academy"
        }
      },
      {
        "@type": "Course",
        "position": 4,
        "name": "Derivatives Exam",
        "description": "Certification for options, futures, and derivatives trading professionals",
        "provider": {
          "@type": "EducationalOrganization",
          "name": "Vitta Finance Academy"
        }
      }
    ]
  };

  const faqData = [
    {
      question: "Which CIRO exam should I take first?",
      answer: "Most candidates start with the CIRE (Canadian Investment Regulatory Exam) as it provides foundational knowledge for investment representatives. After passing CIRE, you can pursue RSE for retail advisory roles or ISE for institutional positions."
    },
    {
      question: "What is the difference between CIRE and RSE exams?",
      answer: "CIRE is the entry-level exam for becoming a Registered Representative or Investment Representative, covering 100 questions in 2.5 hours. RSE is more advanced, designed for investment advisors serving retail clients, with 125 questions over 3 hours and deeper coverage of portfolio management and suitability."
    },
    {
      question: "Do I need to pass CIRE before taking RSE?",
      answer: "While not always mandatory, completing CIRE or equivalent foundational training is highly recommended before attempting RSE, as RSE builds upon concepts covered in CIRE."
    },
    {
      question: "What is the ISE exam for?",
      answer: "The ISE (Institutional Securities Exam) is designed for professionals who will work with institutional clients such as pension funds, corporations, and other large entities. It focuses on institutional trading, compliance, and client service."
    },
    {
      question: "When should I take the Derivatives exam?",
      answer: "The Derivatives exam should be taken after completing either RSE or ISE, as it requires foundational securities knowledge. It's required for anyone who wants to advise on or trade options, futures, and other derivative products."
    },
    {
      question: "Which CIRO exam is the hardest?",
      answer: "The Derivatives exam is generally considered the most challenging due to its mathematical content and complex trading strategies. RSE is considered more difficult than CIRE due to its broader scope and deeper analytical requirements."
    },
    {
      question: "How much do CIRO exams cost?",
      answer: "CIRO exam fees vary but typically range from $200-$400 CAD per attempt. Vitta Finance Academy's prep courses are $29.99/month with full access to all study materials, mock exams, and AI tutor support."
    },
    {
      question: "Can I take multiple CIRO exams?",
      answer: "Yes, many professionals hold multiple CIRO certifications. A common path is CIRE → RSE → Derivatives for retail advisors, or CIRE → ISE → Derivatives for institutional professionals."
    }
  ];

  const exams = [
    {
      name: 'CIRE',
      fullName: 'Canadian Investment Regulatory Exam',
      icon: <Users size={32} />,
      color: '#1e3a8a',
      questions: 100,
      duration: '2.5 hours',
      passingScore: 'Set by CIRO',
      targetAudience: 'Entry-level investment professionals',
      careers: ['Registered Representative (RR)', 'Investment Representative (IR)', 'Client Service Associate'],
      topics: ['Securities fundamentals', 'Regulatory framework', 'Client accounts', 'Order types', 'Ethics & compliance'],
      difficulty: 'Foundation',
      studyTime: '6-8 weeks',
      link: '/cire-exam',
      prerequisites: 'None',
      salary: '$45,000 - $75,000 CAD'
    },
    {
      name: 'RSE',
      fullName: 'Retail Securities Exam',
      icon: <Briefcase size={32} />,
      color: '#059669',
      questions: 125,
      duration: '3 hours',
      passingScore: 'Set by CIRO',
      targetAudience: 'Investment advisors serving retail clients',
      careers: ['Investment Advisor', 'Financial Planner', 'Wealth Manager', 'Portfolio Manager (Retail)'],
      topics: ['Portfolio management', 'Suitability assessment', 'Tax planning', 'Retirement strategies', 'Risk management'],
      difficulty: 'Intermediate',
      studyTime: '8-10 weeks',
      link: '/rse-exam',
      prerequisites: 'CIRE or equivalent recommended',
      salary: '$60,000 - $150,000+ CAD'
    },
    {
      name: 'ISE',
      fullName: 'Institutional Securities Exam',
      icon: <Building2 size={32} />,
      color: '#7c3aed',
      questions: 100,
      duration: '2.5 hours',
      passingScore: 'Set by CIRO',
      targetAudience: 'Professionals serving institutional clients',
      careers: ['Institutional Sales', 'Corporate Advisor', 'Pension Fund Specialist', 'Trading Desk Analyst'],
      topics: ['Institutional trading', 'Fixed income', 'Equity markets', 'Compliance', 'Client management'],
      difficulty: 'Intermediate',
      studyTime: '6-8 weeks',
      link: '/ise-exam',
      prerequisites: 'CIRE or equivalent recommended',
      salary: '$70,000 - $200,000+ CAD'
    },
    {
      name: 'Derivatives',
      fullName: 'Derivatives Fundamentals Exam',
      icon: <TrendingUp size={32} />,
      color: '#dc2626',
      questions: 120,
      duration: '3 hours',
      passingScore: 'Set by CIRO',
      targetAudience: 'Options & futures trading professionals',
      careers: ['Options Trader', 'Derivatives Analyst', 'Risk Manager', 'Hedge Fund Analyst'],
      topics: ['Options strategies', 'Futures contracts', 'Hedging', 'Greeks & pricing', 'Regulatory requirements'],
      difficulty: 'Advanced',
      studyTime: '8-10 weeks',
      link: '/derivatives-exam',
      prerequisites: 'RSE or ISE recommended',
      salary: '$80,000 - $250,000+ CAD'
    }
  ];

  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title="CIRO Exam Comparison 2025: CIRE vs RSE vs ISE vs Derivatives | Complete Guide"
        description="Compare all CIRO certification exams: CIRE, RSE, ISE, and Derivatives. See exam formats, questions, duration, career paths, salary ranges, and which exam is right for you. Expert guide by Vitta Finance Academy."
        keywords="CIRO exam comparison, CIRE vs RSE, CIRE vs ISE, RSE vs Derivatives, which CIRO exam to take, CIRO certification path, CIRO exam difficulty, CIRO exam requirements, best CIRO exam for career, CIRO exam salary, investment representative exam, securities exam Canada"
        ogTitle="CIRO Exam Comparison Guide 2025 - Which Exam Should You Take?"
        ogDescription="Complete comparison of CIRE, RSE, ISE, and Derivatives exams. Find the right CIRO certification for your career in Canadian finance."
        structuredData={comparisonStructuredData}
        faqData={faqData}
      />

      {/* Hero Section */}
      <section style={{
        padding: '80px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: 900,
            lineHeight: 1.1,
            marginBottom: '24px',
            color: 'white'
          }}>
            CIRO Exam Comparison Guide
          </h1>
          <p style={{
            fontSize: '1.25rem',
            marginBottom: '16px',
            maxWidth: '800px',
            margin: '0 auto 16px',
            opacity: 0.95
          }}>
            Not sure which CIRO exam to take? Compare all four certifications side-by-side to find the right path for your career in Canadian finance.
          </p>
          <p style={{
            fontSize: '1rem',
            opacity: 0.85,
            marginBottom: '32px'
          }}>
            Trusted by 30,000+ learners across Canada
          </p>
        </div>
      </section>

      {/* Quick Comparison Table */}
      <section style={{ padding: '96px 0' }}>
        <div className="container">
          <h2 className="heading-2" style={{ 
            marginBottom: '16px', 
            textAlign: 'center',
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}>
            Quick Comparison Table
          </h2>
          <p className="body-large" style={{ 
            marginBottom: '48px', 
            textAlign: 'center',
            color: 'var(--text-secondary)',
            maxWidth: '700px',
            margin: '0 auto 48px',
            padding: '0 16px'
          }}>
            All CIRO exams at a glance - formats, requirements, and career outcomes
          </p>

          <div style={{ 
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            margin: '0 -16px',
            padding: '0 16px'
          }}>
            <table style={{
              width: '100%',
              minWidth: '600px',
              borderCollapse: 'collapse',
              backgroundColor: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--brand-primary)', color: 'white' }}>
                  <th style={{ padding: '16px 12px', textAlign: 'left', fontWeight: 700, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>Feature</th>
                  {exams.map(exam => (
                    <th key={exam.name} style={{ padding: '16px 12px', textAlign: 'center', fontWeight: 700, fontSize: '0.9rem' }}>
                      {exam.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={{ padding: '12px', fontWeight: 600, backgroundColor: 'var(--neutral-light)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Questions</td>
                  {exams.map(exam => (
                    <td key={exam.name} style={{ padding: '12px', textAlign: 'center', fontSize: '0.9rem' }}>{exam.questions}</td>
                  ))}
                </tr>
                <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={{ padding: '12px', fontWeight: 600, backgroundColor: 'var(--neutral-light)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Duration</td>
                  {exams.map(exam => (
                    <td key={exam.name} style={{ padding: '12px', textAlign: 'center', fontSize: '0.9rem' }}>{exam.duration}</td>
                  ))}
                </tr>
                <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={{ padding: '12px', fontWeight: 600, backgroundColor: 'var(--neutral-light)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Passing Score</td>
                  {exams.map(exam => (
                    <td key={exam.name} style={{ padding: '12px', textAlign: 'center', fontSize: '0.9rem' }}>{exam.passingScore}</td>
                  ))}
                </tr>
                <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={{ padding: '12px', fontWeight: 600, backgroundColor: 'var(--neutral-light)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Difficulty</td>
                  {exams.map(exam => (
                    <td key={exam.name} style={{ padding: '12px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: exam.difficulty === 'Foundation' ? '#dbeafe' : 
                                        exam.difficulty === 'Intermediate' ? '#fef3c7' : '#fee2e2',
                        color: exam.difficulty === 'Foundation' ? '#1e40af' : 
                               exam.difficulty === 'Intermediate' ? '#92400e' : '#991b1b'
                      }}>
                        {exam.difficulty}
                      </span>
                    </td>
                  ))}
                </tr>
                <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={{ padding: '12px', fontWeight: 600, backgroundColor: 'var(--neutral-light)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Study Time</td>
                  {exams.map(exam => (
                    <td key={exam.name} style={{ padding: '12px', textAlign: 'center', fontSize: '0.9rem' }}>{exam.studyTime}</td>
                  ))}
                </tr>
                <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={{ padding: '12px', fontWeight: 600, backgroundColor: 'var(--neutral-light)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Prerequisites</td>
                  {exams.map(exam => (
                    <td key={exam.name} style={{ padding: '12px', textAlign: 'center', fontSize: '0.8rem' }}>{exam.prerequisites}</td>
                  ))}
                </tr>
                <tr>
                  <td style={{ padding: '12px', fontWeight: 600, backgroundColor: 'var(--neutral-light)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Salary Range*</td>
                  {exams.map(exam => (
                    <td key={exam.name} style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: 'var(--brand-primary)', fontSize: '0.85rem' }}>{exam.salary}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Salary Disclaimer */}
          <div style={{
            marginTop: '24px',
            padding: '16px 20px',
            backgroundColor: 'var(--neutral-light)',
            borderRadius: '8px',
            borderLeft: '4px solid var(--brand-primary)'
          }}>
            <p style={{ 
              fontSize: '0.85rem', 
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
              margin: 0
            }}>
              <strong>*Salary Disclaimer:</strong> Salary ranges are estimates based on data from{' '}
              <a href="https://www.glassdoor.ca" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-primary)' }}>Glassdoor</a>,{' '}
              <a href="https://www.indeed.ca" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-primary)' }}>Indeed Canada</a>, and{' '}
              <a href="https://www.payscale.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-primary)' }}>PayScale</a>{' '}
              for Canadian finance professionals (2024-2025). Actual compensation varies significantly based on employer, location, experience level, performance, and market conditions. 
              These figures are provided for informational purposes only and should not be considered guaranteed earnings.
            </p>
          </div>
        </div>
      </section>

      {/* Detailed Exam Cards */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--neutral-light)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ 
            marginBottom: '16px', 
            textAlign: 'center',
            wordWrap: 'break-word'
          }}>
            Detailed Exam Breakdown
          </h2>
          <p className="body-large" style={{ 
            marginBottom: '48px', 
            textAlign: 'center',
            color: 'var(--text-secondary)',
            maxWidth: '700px',
            margin: '0 auto 48px',
            padding: '0 16px'
          }}>
            Everything you need to know about each CIRO certification
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 450px), 1fr))',
            gap: '24px'
          }}>
            {exams.map((exam, idx) => (
              <div key={idx} style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid var(--border-light)'
              }}>
                {/* Header */}
                <div style={{
                  backgroundColor: exam.color,
                  padding: '24px 32px',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  {exam.icon}
                  <div>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '4px' }}>{exam.name}</h3>
                    <p style={{ opacity: 0.9, fontSize: '0.95rem' }}>{exam.fullName}</p>
                  </div>
                </div>

                {/* Content */}
                <div style={{ padding: '32px' }}>
                  {/* Quick Stats */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '16px',
                    marginBottom: '24px',
                    padding: '20px',
                    backgroundColor: 'var(--neutral-light)',
                    borderRadius: '12px'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: '1.5rem', fontWeight: 900, color: exam.color }}>{exam.questions}</p>
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Questions</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: '1.5rem', fontWeight: 900, color: exam.color }}>{exam.duration}</p>
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Duration</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: '1.5rem', fontWeight: 900, color: exam.color }}>{exam.passingScore}</p>
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>To Pass</p>
                    </div>
                  </div>

                  {/* Target Audience */}
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Best For
                    </h4>
                    <p style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{exam.targetAudience}</p>
                  </div>

                  {/* Career Paths */}
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase' }}>
                      Career Paths
                    </h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {exam.careers.map((career, cIdx) => (
                        <span key={cIdx} style={{
                          padding: '6px 12px',
                          backgroundColor: 'var(--neutral-light)',
                          borderRadius: '20px',
                          fontSize: '0.85rem',
                          color: 'var(--text-secondary)'
                        }}>
                          {career}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Topics Covered */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase' }}>
                      Key Topics
                    </h4>
                    <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      {exam.topics.map((topic, tIdx) => (
                        <li key={tIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CheckCircle size={16} color={exam.color} />
                          <span style={{ fontSize: '0.9rem' }}>{topic}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA */}
                  <Link 
                    to={exam.link} 
                    className="btn-primary"
                    style={{ width: '100%', textAlign: 'center', backgroundColor: exam.color, borderColor: exam.color }}
                  >
                    Learn More About {exam.name} <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Path Section */}
      <section style={{ padding: '96px 0' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '16px', textAlign: 'center' }}>
            Recommended Certification Paths
          </h2>
          <p className="body-large" style={{ 
            marginBottom: '48px', 
            textAlign: 'center',
            color: 'var(--text-secondary)',
            maxWidth: '700px',
            margin: '0 auto 48px'
          }}>
            Choose your path based on your career goals in Canadian finance
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            {/* Retail Path */}
            <div style={{
              padding: '32px',
              backgroundColor: 'white',
              borderRadius: '16px',
              border: '2px solid var(--border-light)',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                backgroundColor: 'rgba(5, 150, 105, 0.1)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Briefcase size={32} color="#059669" />
              </div>
              <h3 className="heading-4" style={{ marginBottom: '12px' }}>Retail Advisory Path</h3>
              <p className="body-small" style={{ marginBottom: '24px', color: 'var(--text-secondary)' }}>
                For those wanting to advise individual investors and families
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                <span style={{ padding: '8px 16px', backgroundColor: '#1e3a8a', color: 'white', borderRadius: '8px', fontWeight: 600 }}>CIRE</span>
                <ArrowRight size={20} color="var(--text-muted)" />
                <span style={{ padding: '8px 16px', backgroundColor: '#059669', color: 'white', borderRadius: '8px', fontWeight: 600 }}>RSE</span>
                <ArrowRight size={20} color="var(--text-muted)" />
                <span style={{ padding: '8px 16px', backgroundColor: '#dc2626', color: 'white', borderRadius: '8px', fontWeight: 600 }}>Derivatives</span>
              </div>
            </div>

            {/* Institutional Path */}
            <div style={{
              padding: '32px',
              backgroundColor: 'white',
              borderRadius: '16px',
              border: '2px solid var(--border-light)',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                backgroundColor: 'rgba(124, 58, 237, 0.1)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Building2 size={32} color="#7c3aed" />
              </div>
              <h3 className="heading-4" style={{ marginBottom: '12px' }}>Institutional Path</h3>
              <p className="body-small" style={{ marginBottom: '24px', color: 'var(--text-secondary)' }}>
                For those wanting to work with corporations and institutions
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                <span style={{ padding: '8px 16px', backgroundColor: '#1e3a8a', color: 'white', borderRadius: '8px', fontWeight: 600 }}>CIRE</span>
                <ArrowRight size={20} color="var(--text-muted)" />
                <span style={{ padding: '8px 16px', backgroundColor: '#7c3aed', color: 'white', borderRadius: '8px', fontWeight: 600 }}>ISE</span>
                <ArrowRight size={20} color="var(--text-muted)" />
                <span style={{ padding: '8px 16px', backgroundColor: '#dc2626', color: 'white', borderRadius: '8px', fontWeight: 600 }}>Derivatives</span>
              </div>
            </div>

            {/* Trading Path */}
            <div style={{
              padding: '32px',
              backgroundColor: 'white',
              borderRadius: '16px',
              border: '2px solid var(--border-light)',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                backgroundColor: 'rgba(220, 38, 38, 0.1)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <TrendingUp size={32} color="#dc2626" />
              </div>
              <h3 className="heading-4" style={{ marginBottom: '12px' }}>Trading & Risk Path</h3>
              <p className="body-small" style={{ marginBottom: '24px', color: 'var(--text-secondary)' }}>
                For those focused on derivatives and risk management
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                <span style={{ padding: '8px 16px', backgroundColor: '#1e3a8a', color: 'white', borderRadius: '8px', fontWeight: 600 }}>CIRE</span>
                <ArrowRight size={20} color="var(--text-muted)" />
                <span style={{ padding: '8px 16px', backgroundColor: '#059669', color: 'white', borderRadius: '8px', fontWeight: 600 }}>RSE/ISE</span>
                <ArrowRight size={20} color="var(--text-muted)" />
                <span style={{ padding: '8px 16px', backgroundColor: '#dc2626', color: 'white', borderRadius: '8px', fontWeight: 600 }}>Derivatives</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--neutral-light)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '16px', textAlign: 'center' }}>
            Frequently Asked Questions
          </h2>
          <p className="body-large" style={{ 
            marginBottom: '48px', 
            textAlign: 'center',
            color: 'var(--text-secondary)',
            maxWidth: '600px',
            margin: '0 auto 48px'
          }}>
            Common questions about CIRO certifications
          </p>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {faqData.map((faq, idx) => (
              <details 
                key={idx}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '1px solid var(--border-light)',
                  overflow: 'hidden'
                }}
              >
                <summary style={{
                  padding: '20px 24px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  color: 'var(--text-primary)',
                  listStyle: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  {faq.question}
                  <span style={{ 
                    fontSize: '1.5rem', 
                    color: 'var(--brand-primary)',
                    fontWeight: 400
                  }}>+</span>
                </summary>
                <div style={{
                  padding: '0 24px 20px',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.7
                }}>
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '96px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div className="container">
          <Award size={48} style={{ marginBottom: '24px', opacity: 0.9 }} />
          <h2 className="heading-2" style={{ marginBottom: '24px', color: 'white' }}>
            Ready to Start Your CIRO Certification Journey?
          </h2>
          <p style={{ 
            fontSize: '1.25rem',
            lineHeight: 1.7,
            marginBottom: '40px', 
            maxWidth: '700px', 
            margin: '0 auto 40px',
            color: 'white',
            fontWeight: 500
          }}>
            Join 30,000+ successful candidates who prepared with Vitta Finance Academy. Get access to all courses for just $29.99/month.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="https://www.vittafinance.ca/store" target="_blank" rel="noopener noreferrer" className="btn-gold">
              Browse All Courses <ArrowRight size={20} />
            </a>
            <Link to="/contact" className="btn-secondary" style={{
              backgroundColor: 'white',
              color: 'var(--brand-primary)',
              borderColor: 'white'
            }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ExamComparison;
